import React, { ForwardRefRenderFunction, forwardRef, useMemo } from "react";
//#region types
import { CurrencyEnums, DimensionEnums, FormEnums } from "models/enums";
import { FormFieldDefinitionType, FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { SupplierFormTypes } from "models/formTypes";
import { SupplierRuleFormViewProps } from "models/propTypes/supplierPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region  components
import { GenericForm } from "components/common";
import { useSupplierService } from "serviceHooks";
//#endregion
import { EnumHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SupplierRuleForm: ForwardRefRenderFunction<FormHandles, SupplierRuleFormViewProps> = (props, ref) => {
  const supplierService = useSupplierService();
  const { searchBookingTreeHandler, dimensionAmount } = props;
  const formFields = useMemo(
    (): FormFieldDefinitionType<SupplierFormTypes.Rule> => ({
      dateFrom: {
        label: "",
        type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
        innerFields: {
          dateFrom: {
            label: TranslationKeys.Common.DateFrom,
            type: FormEnums.FormFieldType.DATE,
          },
          dateTo: {
            label: TranslationKeys.Common.DateTo,
            type: FormEnums.FormFieldType.DATE,
          },
        },
      },
      amountLowest: {
        label: "",
        type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
        innerFields: {
          amountLowest: {
            label: TranslationKeys.Common.LowestAmount,
            type: FormEnums.FormFieldType.CURRENCY,
            descriptionText: TranslationKeys.Supplier.LowestAmountHelpText,
            extraProps: {
              currency: CurrencyEnums.CurrencyCode.SEK,
              allowNegative: true,
            },
          },
          amountHighest: {
            label: TranslationKeys.Common.HighestAmount,
            type: FormEnums.FormFieldType.CURRENCY,
            descriptionText: TranslationKeys.Supplier.HighestAmountHelpText,
            extraProps: {
              currency: CurrencyEnums.CurrencyCode.SEK,
            },
          },
        },
      },
      bookingAccountName: {
        label: TranslationKeys.Supplier.PostedAs,
        type: FormEnums.FormFieldType.AUTOCOMPLETE,
        descriptionText: TranslationKeys.Supplier.BookingAccountHelpText,
        extraProps: {
          referenceFieldName: FormFieldConstants.Supplier.Rule.BookingAccountId,
          items: searchBookingTreeHandler,
        },
      },
      rulePeriodName: {
        label: TranslationKeys.Common.Interval,
        type: FormEnums.FormFieldType.AUTOCOMPLETE,
        descriptionText: TranslationKeys.Supplier.RulePeriodHelpText,
        extraProps: {
          referenceFieldName: FormFieldConstants.Supplier.Rule.RulePeriod,
          items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.IncomingInvoiceRulePeriod).map((period) => ({
            id: period,
            value: supplierService.getRulePeriodText(period),
          })),
        },
      },
      dimensions: {
        label: "",
        type: FormEnums.FormFieldType.DIMENSIONSELECTOR,
        descriptionText: TranslationKeys.Supplier.DimensionsHelpText,
        extraProps: {
          type: DimensionEnums.DimensionCategory.IncomingInvoice,
          enableSingleSelectionMode: true,
          amount: dimensionAmount,
          hideSelectionDialogFooter: true,
        },
      },
      active: {
        label: TranslationKeys.Common.Active,
        type: FormEnums.FormFieldType.TOGGLE,
        descriptionText: TranslationKeys.Supplier.ActiveHelpText,
        classes: { formControl: cssExports.inlineLabel },
        hidden: (values: SupplierFormTypes.Rule) => values.id === undefined,
      },
    }),
    []
  );

  return <GenericForm formRef={ref} {...props} fields={formFields} />;
};

export default forwardRef(SupplierRuleForm);
